import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { patientPortal } from '@/api/patientPortal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { usePatientPortalBranding } from './PatientPortalBrandingProvider';

export default function PatientPortalResetPassword() {
  const branding = usePatientPortalBranding();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await patientPortal.resetPassword(token, password);
      setDone(true);
      toast.success('Password updated');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <p className="text-sm text-muted-foreground">Invalid reset link.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-lg ring-1 ring-slate-200 p-8 space-y-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Choose a new password</h1>
          {branding.hospitalName ? (
            <p className="text-sm font-medium text-slate-700 mt-1">{branding.hospitalName}</p>
          ) : null}
          <p className="text-sm text-muted-foreground mt-1">At least 8 characters</p>
        </div>

        {done ? (
          <p className="text-sm text-slate-700">
            Your password has been updated.{' '}
            <Link to="/patient-loyalty/login" className="text-teal-700 underline">
              Sign in
            </Link>
          </p>
        ) : (
          <>
            <div>
              <Label>New password</Label>
              <Input
                type="password"
                className="mt-1 h-11"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div>
              <Label>Confirm password</Label>
              <Input
                type="password"
                className="mt-1 h-11"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <Button
              className="w-full h-11 bg-teal-800 hover:bg-teal-900"
              onClick={submit}
              disabled={loading}
            >
              Save password
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
